import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";

import InputFieldText from "Components/Common/InputFieldText";

import { onlyNumberKeys } from "utils";
import { getOptionValue } from "common/enums";

const BillMeterReadingFormItems = ({
  subscriptionType,
  formik,
  meterReading: {
    tariffId,
    meterNumber,
    previousMeterReading,
    isDemandCharge,
    currentReading,
  },
  index,
  t,
}) => {
  const { handleChange, setFieldValue } = formik;

  const { isSolidWasteSubscriptionType, isDcsSubscriptionType } = useMemo(
    () => ({
      isSolidWasteSubscriptionType:
        subscriptionType ===
        getOptionValue("otherUtilitySubscriptionOptions", "Solid Waste"),
      isDcsSubscriptionType:
        subscriptionType ===
        getOptionValue("otherUtilitySubscriptionOptions", "DCS"),
    }),
    [subscriptionType]
  );

  return (
    <Grid
      container
      columnSpacing={1}
      className="border border-1 pt-2 pe-2 mb-2"
    >
      {!isSolidWasteSubscriptionType && (
        <Grid item xs={12}>
          <Typography className="mb-3 fw-bold" lineHeight="1" variant="h6">
            {t("Meter No")}: {meterNumber}
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} sm={4}>
        <InputFieldText
          label={
            t(
              isSolidWasteSubscriptionType ? "Build Up Area" : "Current Reading"
            ) + "*"
          }
          name={`meterReadingsData.${index}.currentReading`}
          formik={formik}
          onChange={(e) => {
            handleChange(e);
            setFieldValue(
              `meterReadingsData.${index}.currentReadingDifference`,
              e.target.value - previousMeterReading.currentReading
            );
          }}
          type="number"
          InputProps={{
            inputProps: { min: 0, onKeyDown: (e) => onlyNumberKeys(e, true) },
          }}
          placeholder="Enter Current Reading"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputFieldText
          label={t("Adjustments") + "*"}
          name={`meterReadingsData.${index}.adjustments`}
          formik={formik}
          type="number"
          InputProps={{
            inputProps: { min: 0, onKeyDown: (e) => onlyNumberKeys(e, true) },
          }}
          placeholder="Enter Adjustments"
        />
      </Grid>

      {isDcsSubscriptionType && (
        <>
          <Grid item xs={12} sm={4}>
            <InputFieldText
              label={t("Pro Rata Consumption") + "*"}
              name={`meterReadingsData.${index}.proRataConsumption`}
              formik={formik}
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              placeholder="Enter Pro Rata Consumption"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputFieldText
              label={t("Actual Max Demand") + "*"}
              name={`meterReadingsData.${index}.actualMaxDemand`}
              formik={formik}
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              placeholder="Enter Actual Max Demand"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputFieldText
              label={t("Return Water Temperature Penalty Charges") + "*"}
              name={`meterReadingsData.${index}.returnWaterTemperaturePenaltyCharges`}
              formik={formik}
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              placeholder="Enter Return Water Temperature Penalty Charges"
            />
          </Grid>
        </>
      )}

      {!isSolidWasteSubscriptionType && (
        <Grid item xs={12} sm={4}>
          <InputFieldText
            label={t("Current Reading Difference")}
            name={`meterReadingsData.${index}.currentReadingDifference`}
            formik={formik}
            type="number"
            disabled
            placeholder="Enter Current Reading Difference"
          />
        </Grid>
      )}

      {isDcsSubscriptionType && (
        <>
          <Grid item xs={12}>
            <InputFieldText
              label={t("Notes")}
              name={`meterReadingsData.${index}.note`}
              formik={formik}
              multiline
              minRows={2}
              maxRows={6}
            />
          </Grid>

          {tariffId === getOptionValue("tariff", "Residential") && (
            <Grid item xs={12} sx={{ mb: 2, mt: -2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    name={`meterReadingsData.${index}.isDemandCharge`}
                    checked={!!isDemandCharge}
                    onChange={() =>
                      setFieldValue(
                        `meterReadingsData.${index}.isDemandCharge`,
                        !isDemandCharge
                      )
                    }
                  />
                }
                label={t("Demand Charges")}
              />
            </Grid>
          )}
        </>
      )}

      {!isSolidWasteSubscriptionType && (
        <>
          <Grid item xs={12}>
            <Typography className="mb-3 " lineHeight="1" variant="h6">
              {t("Previous Readings")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputFieldText
              label={t("Current Reading")}
              name={`meterReadingsData.${index}.previousMeterReading.currentReading`}
              formik={formik}
              onChange={(e) => {
                handleChange(e);
                setFieldValue(
                  `meterReadingsData.${index}.currentReadingDifference`,
                  currentReading - e.target.value
                );
              }}
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              disabled={!previousMeterReading.id}
              type="number"
              placeholder="Enter Current Reading"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputFieldText
              label={t("Adjustments")}
              name={`meterReadingsData.${index}.previousMeterReading.adjustments`}
              formik={formik}
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              disabled
              placeholder="Enter Adjustments"
            />
          </Grid>
        </>
      )}

      {isDcsSubscriptionType && (
        <>
          <Grid item xs={12} sm={4}>
            <InputFieldText
              label={t("Pro Rata Consumption")}
              name={`meterReadingsData.${index}.previousMeterReading.proRataConsumption`}
              formik={formik}
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              disabled
              type="number"
              placeholder="Enter Pro Rata Consumption"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputFieldText
              label={t("Actual Max Demand")}
              name={`meterReadingsData.${index}.previousMeterReading.actualMaxDemand`}
              formik={formik}
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              disabled
              type="number"
              placeholder="Enter Actual Max Demand"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputFieldText
              label={t("Return Water Temperature Penalty Charges")}
              name={`meterReadingsData.${index}.previousMeterReading.returnWaterTemperaturePenaltyCharges`}
              formik={formik}
              disabled
              type="number"
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              placeholder="Enter Return Water Temperature Penalty Charges	"
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

BillMeterReadingFormItems.propTypes = {
  formik: PropTypes.object,
  meterReading: PropTypes.object,
  subscriptionType: PropTypes.number,
  index: PropTypes.number,
  t: PropTypes.func,
};
export default withTranslation()(BillMeterReadingFormItems);
